import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { messaging, getToken } from "../firebase-config";
import { setFcmToken } from "../redux/userSlice";

// BROWSER DETECTOR HELPER
const getBrowserEnvironment = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isIOS =
    /ipad|iphone|ipod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(ua);
  const isMac = /macintosh|mac os x/.test(ua) && !isIOS;
  const isWindows = /windows/.test(ua);
  const isChrome = /chrome|crios/.test(ua) && !/edge|edg|opr/.test(ua);
  const isSafari = /safari/.test(ua) && !isChrome;

  if (isIOS) return "ios";
  if (isIOS && isSafari) return "ios-safari";
  if (isIOS && isChrome) return "ios-chrome";
  if (isAndroid && isChrome) return "android-chrome";
  if (isMac && isChrome) return "mac-chrome";
  if (isMac && isSafari) return "mac-safari";
  if (isWindows && isChrome) return "windows-chrome";

  return "default";
};

// DYNAMIC INSTRUCTION COMPONENTS
const LocationInstructions = ({ env }) => {
  const whitePaw = (
    <span style={{ filter: "brightness(0) invert(1)" }}>🐾</span>
  );
  switch (env) {
    case "mac-chrome":
    case "windows-chrome":
      return (
        <>
          To see pups in your hood, please enable location access by clicking
          the padlock (🔒) or tune icon next to the URL and navigating to:
          <br />
          <br />
          <strong>Site Settings → Location → Allow</strong> (then refresh!)
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    case "mac-safari":
      return (
        <>
          To see pups in your hood, please enable location access by navigating
          to:
          <br />
          <br />
          <strong>Safari → Settings → Websites → Location → Allow</strong>
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    case "ios-safari":
      return (
        <>
          To see pups in your hood, please enable location access by navigating
          to:
          <br />
          <br />
          <strong>
            Settings → Privacy & Security → Location Services → Safari Websites
            → While Using the App
          </strong>
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    case "ios-chrome":
      return (
        <>
          To see pups in your hood, please enable location access by tap the:
          <br />
          <br />
          <strong>
            "aA" icon in the address bar → Website Settings → Location → Allow
          </strong>
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    case "android-chrome":
      return (
        <>
          To see pups in your hood, please enable location access by tapping the
          padlock (🔒) or tune icon next to the URL and navigating to:
          <br />
          <br />
          <strong>Permissions → Location → Allow</strong>
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    default:
      return (
        <>
          To see pups in your hood, please enable location access in your
          browser's site settings or your device's privacy settings.
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
  }
};

const NotificationInstructions = ({ env, isStandalone }) => {
  const whitePaw = (
    <span style={{ filter: "brightness(0) invert(1)" }}>🐾</span>
  );
  switch (env) {
    case "mac-chrome":
    case "windows-chrome":
      return (
        <>
          To get updates about pups in your hood, please enable notifications by
          clicking the padlock (🔒) or tune icon next to the URL and navigating
          to:
          <br />
          <br />
          <strong>Site Settings → Notifications → Allow</strong> (then refresh!)
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    case "mac-safari":
      return (
        <>
          To get updates about pups in your hood, please enable notifications by
          navigating to:
          <br />
          <br />
          <strong>Safari → Settings → Websites → Notifications → Allow</strong>
          <br />
          <br />
          (Ensure System Settings → Notifications → Safari is also enabled)
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    case "ios-safari":
    case "ios-chrome":
      if (isStandalone) {
        return (
          <>
            To get updates about pups in your hood, please enable notifications
            in your device settings:
            <br />
            <br />
            <strong>
              Settings → Social Paws → Notifications → Allow Notifications
            </strong>
            <br />
            <br />
            Thank you! {whitePaw}
          </>
        );
      }
      return (
        <>
          To get updates about pups in your hood, you'll need to install our
          app!
          <br />
          <br />
          <strong>Tap the Share icon → Add to Home Screen</strong>
          <br />
          <br />
          Open the app from your home screen, then allow notifications when
          prompted.
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    case "android-chrome":
      return (
        <>
          To get updates about pups in your hood, please enable notifications by
          tapping the padlock (🔒) or tune icon next to the URL and navigating
          to:
          <br />
          <br />
          <strong>Permissions → Notifications → Allow</strong>
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
    default:
      return (
        <>
          To get updates about pups in your hood, please enable notifications in
          your browser's site settings or your device's notification settings.
          <br />
          <br />
          Thank you! {whitePaw}
        </>
      );
  }
};

const PermissionEnforcer = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const [locStatus, setLocStatus] = useState("checking");

  // Synchronously initialize the notification state to prevent UI flashes
  const [notifStatus, setNotifStatus] = useState(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      return Notification.permission;
    }
    return "checking";
  });

  const [isLocHovered, setIsLocHovered] = useState(false);
  const [isNotifHovered, setIsNotifHovered] = useState(false);

  // Track if the user just tried to enable notifications and native prompt was shown
  const [hasRequestedNotif, setHasRequestedNotif] = useState(false);

  const [isLocDismissed, setIsLocDismissed] = useState(
    localStorage.getItem("locBannerDismissed") === "true",
  );
  const [isNotifDismissed, setIsNotifDismissed] = useState(
    localStorage.getItem("notifBannerDismissed") === "true",
  );

  const env = getBrowserEnvironment();

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  // Do not show notification banner AT ALL on iOS Safari browsers (non-PWA)
  const hideNotifBanner = env === "ios" && !isStandalone;

  useEffect(() => {
    let isMounted = true;
    let locPermStatus = null;
    let notifPermStatus = null;
    let pollInterval = null;

    // Listen for custom event fired when geolocation fetch successfully triggers across the app
    const handleLocationGrantedEvent = () => {
      if (isMounted) setLocStatus("granted");
    };
    window.addEventListener("locationGranted", handleLocationGrantedEvent);

    const checkPermissions = async () => {
      // LOCATION CHECK
      if (navigator.permissions && navigator.permissions.query) {
        try {
          locPermStatus = await navigator.permissions.query({
            name: "geolocation",
          });
          if (isMounted) setLocStatus(locPermStatus.state);

          // Add listener to dismiss banner automatically if user toggles permissions
          locPermStatus.onchange = () => {
            if (isMounted) setLocStatus(locPermStatus.state);
          };
        } catch (e) {
          if (isMounted) setLocStatus("prompt");
        }
      } else {
        if (isMounted) setLocStatus("prompt");
      }

      // NOTIFICATIONS CHECK
      if (typeof window === "undefined" || !("Notification" in window)) {
        if (isMounted) setNotifStatus("denied");
        return;
      }

      let currentPerm = Notification.permission;
      const ua = navigator.userAgent.toLowerCase();
      const isIOS = /iphone|ipad|ipod/.test(ua);

      if (isIOS && !isStandalone && currentPerm !== "granted") {
        if (isMounted) setNotifStatus("denied");
        return;
      }

      // Use the async Permissions API for live sync events
      if (navigator.permissions && navigator.permissions.query) {
        try {
          notifPermStatus = await navigator.permissions.query({
            name: "notifications",
          });

          if (currentPerm !== "granted") {
            currentPerm =
              notifPermStatus.state === "prompt"
                ? "default"
                : notifPermStatus.state;
          }

          notifPermStatus.onchange = () => {
            if (isMounted) {
              let updatedState =
                notifPermStatus.state === "prompt"
                  ? "default"
                  : notifPermStatus.state;

              if (Notification.permission === "granted") {
                updatedState = "granted";
              }

              setNotifStatus(updatedState);
              if (updatedState === "granted" && messaging) {
                registerFCM();
              }
            }
          };
        } catch (e) {
          console.warn("Permissions API for notifications not supported", e);
        }
      }

      if (isMounted) {
        setNotifStatus(currentPerm);
      }

      if (currentPerm === "granted" && messaging) {
        registerFCM();
      }
    };

    const registerFCM = async () => {
      try {
        const registration = await navigator.serviceWorker.register("/sw.js", {
          scope: "/",
        });
        await navigator.serviceWorker.ready;
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (token && isMounted) {
          dispatch(setFcmToken(token));
        }
      } catch (err) {
        console.warn("Failed to fetch FCM token on load:", err);
      }
    };

    checkPermissions();

    // Fallback interval logic for iOS Safari & PWAs where onchange/promises drop
    pollInterval = setInterval(async () => {
      if (!isMounted) return;

      let isLocDenied = false;

      // Manually poll the Permissions API for hidden state updates (Location)
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const queryStatus = await navigator.permissions.query({
            name: "geolocation",
          });
          if (isMounted) setLocStatus(queryStatus.state);
          if (queryStatus.state === "denied") isLocDenied = true;
        } catch (e) {}
      }

      // Absolute Fallback: if coordinates exist in storage and not explicitly denied, auto-clear banner
      if (
        isMounted &&
        !isLocDenied &&
        localStorage.getItem("user_location_coords")
      ) {
        setLocStatus("granted");
      }

      // Manually poll Notification permission
      if (typeof window !== "undefined" && "Notification" in window) {
        const currentNotifPerm = Notification.permission;
        if (isMounted) {
          if (currentNotifPerm === "granted") {
            setNotifStatus("granted");
          } else if (currentNotifPerm === "denied") {
            setNotifStatus("denied");
          }
        }
      }
    }, 1500);

    return () => {
      isMounted = false;
      window.removeEventListener("locationGranted", handleLocationGrantedEvent);
      if (locPermStatus) locPermStatus.onchange = null;
      if (notifPermStatus) notifPermStatus.onchange = null;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [dispatch, isStandalone]);

  const requestNotificationsUserGesture = async () => {
    if (!("Notification" in window)) return;

    try {
      setHasRequestedNotif(true);
      const permission = await new Promise((resolve) => {
        Notification.requestPermission(resolve);
      });

      setNotifStatus(permission);

      if (permission === "granted" && messaging) {
        const registration = await navigator.serviceWorker.register("/sw.js", {
          scope: "/",
        });
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
        if (token) dispatch(setFcmToken(token));
      }
    } catch (error) {
      console.warn("Failed to request permissions", error);
    }
  };

  const handleDismissLocation = (e) => {
    e.stopPropagation();
    setIsLocDismissed(true);
    localStorage.setItem("locBannerDismissed", "true");
  };

  const handleDismissNotification = (e) => {
    e.stopPropagation();
    setIsNotifDismissed(true);
    localStorage.setItem("notifBannerDismissed", "true");
  };

  const bannerContainerStyle = {
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    alignItems: "flex-end",
  };

  const alertBoxStyle = {
    backgroundColor: "#ffebee",
    border: "1px solid #d32f2f",
    color: "#c62828",
    padding: "6px 10px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    cursor: "help",
    position: "relative",
    boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  };

  const closeBtnStyle = {
    background: "none",
    border: "none",
    color: "#c62828",
    fontSize: "20px",
    lineHeight: "1",
    cursor: "pointer",
    padding: "0 4px",
    fontWeight: "bold",
    marginBottom: "2px",
  };

  const tooltipStyle = {
    visibility: "hidden",
    width: "320px",
    backgroundColor: "#000",
    color: "#fff",
    textAlign: "left",
    borderRadius: "8px",
    padding: "15px",
    position: "absolute",
    zIndex: 10,
    top: "110%",
    right: "0",
    fontSize: "13px",
    lineHeight: "1.5",
    boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
    transition: "visibility 0.2s, opacity 0.2s",
  };

  const enableButtonStyle = {
    marginTop: "10px",
    padding: "5px 10px",
    backgroundColor: "#ffb74d",
    border: "none",
    borderRadius: "4px",
    color: "#000",
    cursor: "pointer",
    fontWeight: "bold",
    width: "100%",
  };

  // Only show the "Enable Now" button if we are in the default state
  // AND the user hasn't just clicked the button to trigger a prompt they dismissed/denied.
  const showEnableButton = notifStatus === "default" && !hasRequestedNotif;

  return (
    <div style={bannerContainerStyle}>
      {/* LOCATION WARNING */}
      {locStatus !== "checking" &&
        locStatus !== "granted" &&
        !isLocDismissed && (
          <div
            style={alertBoxStyle}
            onMouseEnter={() => setIsLocHovered(true)}
            onMouseLeave={() => setIsLocHovered(false)}
          >
            <span>⚠️ Location Access Required.</span>
            <button
              style={closeBtnStyle}
              onClick={handleDismissLocation}
              title="Dismiss"
            >
              &times;
            </button>
            <div
              style={{
                ...tooltipStyle,
                visibility: isLocHovered ? "visible" : "hidden",
                opacity: isLocHovered ? 1 : 0,
              }}
            >
              <strong style={{ color: "#ffb74d" }}>
                How to enable Location:
              </strong>
              <br />
              <br />
              <LocationInstructions env={env} />
            </div>
          </div>
        )}

      {/* NOTIFICATION WARNING */}
      {!hideNotifBanner &&
        notifStatus !== "checking" &&
        notifStatus !== "granted" &&
        !isNotifDismissed && (
          <div
            style={alertBoxStyle}
            onMouseEnter={() => setIsNotifHovered(true)}
            onMouseLeave={() => setIsNotifHovered(false)}
          >
            <span>⚠️ Notification Access Required.</span>
            <button
              style={closeBtnStyle}
              onClick={handleDismissNotification}
              title="Dismiss"
            >
              &times;
            </button>
            <div
              style={{
                ...tooltipStyle,
                visibility: isNotifHovered ? "visible" : "hidden",
                opacity: isNotifHovered ? 1 : 0,
              }}
            >
              {showEnableButton ? (
                <>
                  <strong style={{ color: "#ffb74d" }}>
                    Get updates about pups in your hood!
                  </strong>
                  <br />
                  <br />
                  <button
                    style={enableButtonStyle}
                    onClick={requestNotificationsUserGesture}
                  >
                    Click Here to Enable Now
                  </button>
                </>
              ) : (
                <>
                  <strong style={{ color: "#ffb74d" }}>
                    How to enable Notifications:
                  </strong>
                  <br />
                  <br />
                  <NotificationInstructions
                    env={env}
                    isStandalone={isStandalone}
                  />
                </>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default PermissionEnforcer;
